import { supabase } from '../lib/supabase';
import { getShipmentPhotoUrl } from './shipmentStorageService';

// ✅ PHONE NUMBER NORMALIZATION UTILITIES
/**
 * Normalizes phone numbers to handle both international and local formats
 * Converts: 060303025 → +3360303025 or +33698765433 → +33698765433
 * Handles: French phone numbers with country code +33
 */
const normalizePhoneNumber = (phone) => {
  if (!phone || typeof phone !== 'string') return null;
  
  // Remove all spaces, dashes, parentheses, and dots
  let cleaned = phone?.replace(/[\s\-()\.]/g, '');
  
  // If already in international format (+33...), return as-is
  if (cleaned?.startsWith('+33')) {
    return cleaned;
  }
  
  // If starts with 0 (French local format), convert to international
  if (cleaned?.startsWith('0')) {
    // Remove leading 0 and add +33
    return '+33' + cleaned?.substring(1);
  }
  
  // If starts with 33 (without +), add +
  if (cleaned?.startsWith('33')) {
    return '+' + cleaned;
  }
  
  // If it's just digits without country code, assume French and add +33
  if (/^\d+$/?.test(cleaned)) {
    return '+33' + cleaned;
  }
  
  return cleaned; // Return as-is if no pattern matches
};

/**
 * Searches for client by phone number, trying multiple formats and fuzzy matching
 */
const findClientByPhone = async (phone) => {
  // Normalize the input phone number
  const normalizedPhone = normalizePhoneNumber(phone);
  
  // Try exact match first (normalized format)
  let { data: clients, error } = await supabase
    ?.from('clients')
    ?.select('id, phone, loyalty_points_total, name')
    ?.eq('phone', normalizedPhone);
  
  if (error) throw error;
  
  // If found, return immediately
  if (clients && clients?.length > 0) {
    return clients?.[0];
  }
  
  // If not found, try original format (in case DB has different format)
  ({ data: clients, error } = await supabase
    ?.from('clients')
    ?.select('id, phone, loyalty_points_total, name')
    ?.eq('phone', phone));
  
  if (error) throw error;
  
  if (clients && clients?.length > 0) {
    return clients?.[0];
  }
  
  // Try without country code (local format search)
  const localFormat = normalizedPhone?.replace('+33', '0');
  ({ data: clients, error } = await supabase
    ?.from('clients')
    ?.select('id, phone, loyalty_points_total, name')
    ?.eq('phone', localFormat));
  
  if (error) throw error;
  
  if (clients && clients?.length > 0) {
    return clients?.[0];
  }

  // Try fuzzy matching using LIKE with last 9 digits
  // Extract the last 9 digits (French mobile numbers without country code)
  const phoneDigits = normalizedPhone?.replace(/\D/g, ''); // Remove non-digits
  const last9Digits = phoneDigits?.slice(-9); // Get last 9 digits
  
  ({ data: clients, error } = await supabase
    ?.from('clients')
    ?.select('id, phone, loyalty_points_total, name')
    ?.or(`phone.like.%${last9Digits},phone.like.%${last9Digits}%`));
  
  if (error) throw error;
  
  if (clients && clients?.length > 0) {
    console.warn('⚠️ Phone mismatch detected! Input:', phone, '→ Found in DB:', clients?.[0]?.phone);
    return clients?.[0];
  }

  return null;
};

export const adminService = {
  // Get all shipments with totals and client loyalty points
  async getAllShipmentsWithTotals() {
    // Query shipments table directly with client relationship
    const { data, error } = await supabase
      ?.from('shipments')
      ?.select(`
        *,
        client:client_id (
          name,
          phone,
          email,
          loyalty_points_total
        )
      `)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    // Get shipment items to calculate totals and article summary
    const shipmentsWithTotals = await Promise.all(
      data?.map(async (shipment) => {
        const { data: items } = await supabase
          ?.from('shipment_items')
          ?.select(`
            id,
            price_total,
            quantity,
            catalog_item:catalog_item_id (
              name
            )
          `)
          ?.eq('shipment_id', shipment?.id);

        const totalAmount = items?.reduce((sum, item) => sum + (parseFloat(item?.price_total) || 0), 0) || 0;

        // Build article summary: count or first item name
        let articlesSummary = null;
        if (items && items?.length > 0) {
          if (items?.length === 1) {
            const firstItem = items?.[0];
            const itemName = firstItem?.catalog_item?.name;
            const qty = firstItem?.quantity || 1;
            articlesSummary = itemName ? `${itemName}${qty > 1 ? ` ×${qty}` : ''}` : `1 article`;
          } else {
            const totalQty = items?.reduce((sum, item) => sum + (item?.quantity || 1), 0);
            articlesSummary = `${totalQty} article${totalQty > 1 ? 's' : ''}`;
          }
        }

        return {
          id: shipment?.id,
          reference: shipment?.reference,
          senderName: shipment?.sender_name,
          senderPhone: shipment?.sender_phone,
          senderCity: shipment?.sender_city,
          senderAddress: shipment?.sender_address,
          senderCountry: shipment?.sender_country,
          receiverName: shipment?.receiver_name,
          receiverPhone: shipment?.receiver_phone,
          receiverCity: shipment?.receiver_city,
          receiverAddress: shipment?.receiver_address,
          receiverCountry: shipment?.receiver_country,
          status: shipment?.current_status,
          type: shipment?.shipment_type,
          createdAt: shipment?.created_at,
          updatedAt: shipment?.updated_at,
          estimatedDelivery: shipment?.estimated_delivery_date,
          actualDelivery: shipment?.actual_delivery_date,
          invoiceUrl: shipment?.invoice_url,
          loyaltyPointsEarned: shipment?.loyalty_points_earned,
          totalAmount,
          paymentStatus: shipment?.payment_status || 'non_paye',
          paidAmount: shipment?.paid_amount || 0,
          articlesSummary,
          itemsCount: items?.length || 0,
          clientLoyaltyPoints: shipment?.client?.loyalty_points_total || 0,
          isArchived: shipment?.is_archived || false,
          archivedAt: shipment?.archived_at || null,
        };
      })
    );

    return shipmentsWithTotals;
  },

  // Get shipment with items for detail page
  async getShipmentWithItems(shipmentId) {
    // ✅ CORRECTE: shipmentId is the actual UUID value from params
    console.log('🔍 adminService.getShipmentWithItems - Valeur reçue:', shipmentId);
    
    // ✅ CORRECTE: Query uses actual shipmentId value, not literal ":shipmentId"
    const { data: shipment, error: shipmentError } = await supabase
      ?.from('shipments')
      ?.select('*')
      ?.eq('id', shipmentId)  // ← Uses actual UUID value
      ?.single();

    if (shipmentError) {
      console.error('❌ Erreur Supabase:', shipmentError);
      throw new Error('Envoi non trouvé');
    }

    if (!shipment) {
      throw new Error('Aucun envoi trouvé pour cet identifiant');
    }

    console.log('✅ Envoi trouvé:', shipment?.reference);

    // ✅ CORRECTE: Get related items using actual shipmentId
    const { data: items, error: itemsError } = await supabase
      ?.from('shipment_items')
      ?.select(`
        *,
        catalog_item:catalog_item_id (
          name,
          category,
          description
        )
      `)
      ?.eq('shipment_id', shipmentId);  // ← Uses actual UUID value

    if (itemsError) throw itemsError;

    // ✅ FIX: Use new phone search utility for client lookup
    let client = await findClientByPhone(shipment?.sender_phone);

    // ✅ FIXED: Handle both signed URLs (new shipments) and storage paths (old shipments)
    let signedPhotoUrls = [];
    
    if (shipment?.photos_urls && Array.isArray(shipment?.photos_urls) && shipment?.photos_urls?.length > 0) {
      // Process each URL in the array
      const urlPromises = shipment?.photos_urls?.map(async (urlOrPath) => {
        try {
          if (!urlOrPath || typeof urlOrPath !== 'string' || urlOrPath?.trim()?.length === 0) {
            return null;
          }

          // ✅ CHECK: If it's already a signed URL (starts with https://), use it directly
          if (urlOrPath?.startsWith('https://') || urlOrPath?.startsWith('http://')) {
            console.log('✅ Already a signed URL, using directly:', urlOrPath?.substring(0, 50) + '...');
            return urlOrPath;
          }

          // ✅ CONVERT: It's a storage path, convert to signed URL
          console.log('🔄 Converting storage path to signed URL:', urlOrPath);
          const signedUrl = await getShipmentPhotoUrl(urlOrPath);
          return signedUrl; // Will be null if file doesn't exist
        } catch (error) {
          console.warn('⚠️ Failed to process photo URL/path:', urlOrPath, error?.message);
          return null;
        }
      });

      const urls = await Promise.all(urlPromises);
      // Filter out null values (failed conversions or missing photos)
      signedPhotoUrls = urls?.filter(url => url !== null);
      
      console.log(`✅ Processed ${shipment?.photos_urls?.length} photos, ${signedPhotoUrls?.length} valid URLs`);
    } else if (shipment?.photo_url && typeof shipment?.photo_url === 'string' && shipment?.photo_url?.trim()?.length > 0) {
      // ✅ FALLBACK: Handle single photo_url field
      try {
        if (shipment?.photo_url?.startsWith('https://') || shipment?.photo_url?.startsWith('http://')) {
          // Already a signed URL
          signedPhotoUrls = [shipment?.photo_url];
        } else {
          // Convert storage path to signed URL
          const signedUrl = await getShipmentPhotoUrl(shipment?.photo_url);
          if (signedUrl) {
            signedPhotoUrls = [signedUrl];
          }
        }
      } catch (error) {
        console.warn('⚠️ Failed to process single photo_url');
      }
    }

    // ✅ NEW: Include container_id in returned data
    // Transform flat structure to nested structure expected by component
    return {
      id: shipment?.id,
      reference: shipment?.reference,
      status: shipment?.current_status,
      type: shipment?.shipment_type,
      description: shipment?.description,
      weight: shipment?.weight,
      dimensions: shipment?.dimensions,
      container_id: shipment?.container_id,  // ✅ NEW: Include container_id
      photo_url: signedPhotoUrls?.[0] || null,  // ✅ First photo for backwards compatibility
      photos_urls: signedPhotoUrls,  // ✅ Complete array of valid signed URLs
      invoice_url: shipment?.invoice_url,
      loyalty_points_earned: shipment?.loyalty_points_earned,
      discount_amount: shipment?.discount_amount || 0,  // ✅ CRITICAL FIX: Include discount_amount for invoice generation
      estimated_departure_date: shipment?.estimated_departure_date,  // ✅ NEW: Include estimated_departure_date
      estimated_delivery_date: shipment?.estimated_delivery_date,
      actual_delivery_date: shipment?.actual_delivery_date,
      created_at: shipment?.created_at,
      updated_at: shipment?.updated_at,
      // Nested sender structure
      sender: {
        name: shipment?.sender_name,
        phone: shipment?.sender_phone,
        address: shipment?.sender_address,
        city: shipment?.sender_city,
        country: shipment?.sender_country
      },
      // Nested receiver structure
      receiver: {
        name: shipment?.receiver_name,
        phone: shipment?.receiver_phone,
        address: shipment?.receiver_address,
        city: shipment?.receiver_city,
        country: shipment?.receiver_country,
        commune: shipment?.receiver_commune,
        neighborhood: shipment?.receiver_neighborhood,
        address_complement: shipment?.receiver_address_complement
      },
      items: items?.map(item => ({
        id: item?.id,
        catalogItemId: item?.catalog_item_id,
        itemName: item?.catalog_item?.name,
        category: item?.catalog_item?.category,
        description: item?.catalog_item?.description,
        quantity: item?.quantity,
        priceUnit: item?.price_unit,
        priceTotal: item?.price_total
      })),
      clientLoyaltyPoints: client?.loyalty_points_total || 0,
      // ✅ Payment fields from shipments table
      payment_status: shipment?.payment_status || 'non_paye',
      paid_amount: shipment?.paid_amount || 0,
      payment_method: shipment?.payment_method || null,
      payment_notes: shipment?.payment_notes || null,
      discount_amount: shipment?.discount_amount || 0,
      // Flat fields for direct access
      sender_name: shipment?.sender_name,
      sender_phone: shipment?.sender_phone,
      sender_address: shipment?.sender_address,
      sender_city: shipment?.sender_city,
      sender_country: shipment?.sender_country,
      receiver_name: shipment?.receiver_name,
      receiver_phone: shipment?.receiver_phone,
      receiver_address: shipment?.receiver_address,
      receiver_city: shipment?.receiver_city,
      receiver_country: shipment?.receiver_country,
      receiver_commune: shipment?.receiver_commune,
      receiver_neighborhood: shipment?.receiver_neighborhood,
      receiver_address_complement: shipment?.receiver_address_complement,
      pickup_photo_urls: shipment?.pickup_photo_urls
    };
  },

  // Add item to shipment
  async addShipmentItem(shipmentId, catalogItemId, quantity) {
    console.log('Debug - adminService.addShipmentItem - valeur de shipmentId:', shipmentId);
    console.log('Debug - adminService.addShipmentItem - valeur de catalogItemId:', catalogItemId);

    // ✅ STRICT AUTH CHECK: verify real Supabase session before insert
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    console.log("CURRENT SUPABASE USER:", user, authError);

    if (!user) {
      throw new Error("Session Supabase invalide, veuillez vous reconnecter.");
    }

    console.log("SUPABASE USER ID for insert:", user?.id);
    // Expected for nombo@adtexpress.cloud: 9b7c02a0-0d5e-4459-855f-25181697b9d0
    
    // ✅ REMOVED VALIDATION - Let Supabase handle UUID validation
    // validateUUIDOrThrow(shipmentId, 'ID envoi');
    // validateUUIDOrThrow(catalogItemId, 'ID article');
    
    // Get catalog item price
    const { data: catalogItem, error: catalogError } = await supabase?.from('catalog_items')?.select('price')?.eq('id', catalogItemId)?.single();

    if (catalogError) throw catalogError;

    const priceUnit = parseFloat(catalogItem?.price);
    const priceTotal = priceUnit * quantity;

    const { data, error } = await supabase?.from('shipment_items')?.insert({
        shipment_id: shipmentId,
        catalog_item_id: catalogItemId,
        quantity,
        price_unit: priceUnit,
        price_total: priceTotal
      })?.select()?.single();

    if (error) throw error;
    return data;
  },

  // Remove item from shipment
  async removeShipmentItem(itemId) {
    console.log('Debug - adminService.removeShipmentItem - valeur de itemId:', itemId);

    // ✅ STRICT AUTH CHECK
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    console.log("CURRENT SUPABASE USER (removeShipmentItem):", user, authError);

    if (!user) {
      throw new Error("Session Supabase invalide, veuillez vous reconnecter.");
    }
    
    // ✅ REMOVED VALIDATION - Let Supabase handle UUID validation
    // validateUUIDOrThrow(itemId, 'ID article');
    
    const { error } = await supabase?.from('shipment_items')?.delete()?.eq('id', itemId);

    if (error) throw error;
  },

  // ✅ NEW: Update shipment item (quantity and catalog_item_id)
  async updateShipmentItem(itemId, catalogItemId, quantity) {
    console.log('Debug - adminService.updateShipmentItem:', { itemId, catalogItemId, quantity });

    // ✅ STRICT AUTH CHECK
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    console.log("CURRENT SUPABASE USER (updateShipmentItem):", user, authError);

    if (!user) {
      throw new Error("Session Supabase invalide, veuillez vous reconnecter.");
    }
    
    // Get catalog item price
    const { data: catalogItem, error: catalogError } = await supabase
      ?.from('catalog_items')
      ?.select('price')
      ?.eq('id', catalogItemId)
      ?.single();

    if (catalogError) throw catalogError;

    const priceUnit = parseFloat(catalogItem?.price);
    const priceTotal = priceUnit * quantity;

    const { data, error } = await supabase
      ?.from('shipment_items')
      ?.update({
        catalog_item_id: catalogItemId,
        quantity,
        price_unit: priceUnit,
        price_total: priceTotal
      })
      ?.eq('id', itemId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  // ✅ NEW: Update shipment discount
  async updateShipmentDiscount(shipmentId, discountAmount) {
    console.log('Debug - adminService.updateShipmentDiscount:', { shipmentId, discountAmount });
    
    const { data, error } = await supabase
      ?.from('shipments')
      ?.update({ discount_amount: discountAmount })
      ?.eq('id', shipmentId)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  // Update client loyalty points
  async updateClientLoyaltyPoints(clientPhone, pointsToAdd, senderName = null) {
    console.log('🔍 updateClientLoyaltyPoints - Phone reçu:', clientPhone, 'Nom expéditeur:', senderName);
    
    // ✅ FIX: Use new phone search utility
    let client = await findClientByPhone(clientPhone);

    // ✅ FIXED: If client not found, create with actual sender name
    if (!client) {
      console.log('⚠️ Client not found, creating new client with phone:', clientPhone);
      
      // ✅ Use actual sender name if provided, otherwise use phone-based default
      const clientName = senderName || `Client ${clientPhone}`;
      
      // Create new client with actual name or phone-based default
      const { data: newClient, error: createError } = await supabase
        ?.from('clients')
        ?.insert({
          phone: clientPhone,
          name: clientName,  // ✅ Use actual sender name when available
          loyalty_points_total: 0
        })
        ?.select()
        ?.single();

      if (createError) {
        console.error('❌ Error creating new client:', createError);
        throw new Error(`Impossible de créer le client: ${createError.message}`);
      }

      client = newClient;
      console.log('✅ New client created:', client);
    } else if (senderName && client?.name?.startsWith('Client ')) {
      // ✅ FIXED: If client exists but has phone-based default name, update with actual name
      console.log('🔄 Updating client name from default to actual:', senderName);
      
      const { data: updatedClient, error: updateNameError } = await supabase
        ?.from('clients')
        ?.update({ name: senderName })
        ?.eq('id', client?.id)
        ?.select()
        ?.single();
      
      if (!updateNameError && updatedClient) {
        client = updatedClient;
        console.log('✅ Client name updated successfully');
      }
    }

    const newTotal = (client?.loyalty_points_total || 0) + pointsToAdd;

    console.log('✅ Client trouvé/créé/mis à jour, mise à jour points:', { 
      clientId: client?.id,
      clientPhone: client?.phone,
      clientName: client?.name,
      currentPoints: client?.loyalty_points_total,
      pointsToAdd,
      newTotal 
    });

    // ✅ Update using the actual phone from database (ensures correct format)
    const { data, error } = await supabase
      ?.from('clients')
      ?.update({ loyalty_points_total: newTotal })
      ?.eq('phone', client?.phone)  // Use phone from database
      ?.select();

    if (error) {
      console.error('❌ Erreur mise à jour points fidélité:', error);
      throw error;
    }

    if (!data || data?.length === 0) {
      console.error('❌ Aucune ligne mise à jour');
      throw new Error('Échec de la mise à jour des points de fidélité');
    }

    console.log('✅ Points fidélité mis à jour avec succès:', data?.[0]);
    return data?.[0];
  },

  // Get all clients with shipment counts
  async getAllClientsWithStats() {
    // ✅ STEP 1: Get all unique senders from shipments table (CRITICAL: this ensures we see ALL clients who made shipments)
    const { data: uniqueSenders, error: sendersError } = await supabase
      ?.from('shipments')
      ?.select('sender_name, sender_phone')
      ?.order('created_at', { ascending: false });

    if (sendersError) throw sendersError;

    // ✅ STEP 2: Create a map of unique phone numbers with their most recent sender name
    const uniquePhoneMap = new Map();
    uniqueSenders?.forEach(sender => {
      if (sender?.sender_phone && !uniquePhoneMap?.has(sender?.sender_phone)) {
        uniquePhoneMap?.set(sender?.sender_phone, sender?.sender_name || `Client ${sender?.sender_phone}`);
      }
    });

    // ✅ STEP 3: Get all existing clients (including promo clients and material clients)
    const { data: existingClients, error: clientsError } = await supabase
      ?.from('clients')
      ?.select('*')
      ?.order('created_at', { ascending: false });

    if (clientsError) throw clientsError;

    // ✅ STEP 4: Create missing clients (those who have shipments but no client record)
    const existingPhones = new Set(existingClients?.map(c => c?.phone));
    const missingClients = [];

    for (const [phone, name] of uniquePhoneMap?.entries()) {
      if (!existingPhones?.has(phone)) {
        missingClients?.push({
          phone,
          name,
          loyalty_points_total: 0
        });
      }
    }

    // ✅ STEP 5: Insert missing clients if any
    if (missingClients?.length > 0) {
      const { error: insertError } = await supabase
        ?.from('clients')
        ?.insert(missingClients);

      if (insertError) {
        console.error('❌ Error creating missing clients:', insertError);
      }
    }

    // ✅ STEP 6: Re-fetch all clients after creating missing ones
    const { data: allClients, error: finalError } = await supabase
      ?.from('clients')
      ?.select('*')
      ?.order('created_at', { ascending: false });

    if (finalError) throw finalError;

    // ✅ STEP 7: Build stats for each client (including promo orders, barrel orders, carton orders)
    const clientsWithStats = await Promise.all(
      allClients?.map(async (client) => {
        // ✅ PRIMARY: count shipments via client_id (canonical FK relation)
        const { data: shipmentsByClientId } = await supabase
          ?.from('shipments')
          ?.select('id, sender_name')
          ?.eq('client_id', client?.id)
          ?.eq('is_archived', false);

        // ✅ LEGACY FALLBACK: for old shipments where client_id IS NULL, use phone
        // Only count shipments not already counted via client_id to avoid double-counting
        const { data: legacyShipments } = await supabase
          ?.from('shipments')
          ?.select('id, sender_name')
          ?.is('client_id', null)
          ?.eq('sender_phone', client?.phone)
          ?.eq('is_archived', false);

        // Merge: deduplicate by id (client_id shipments take priority)
        const clientIdSet = new Set((shipmentsByClientId || [])?.map(s => s?.id));
        const uniqueLegacy = (legacyShipments || [])?.filter(s => !clientIdSet?.has(s?.id));
        const shipments = [...(shipmentsByClientId || []), ...uniqueLegacy];

        // Fetch promo orders count for this client
        const { count: promoCount } = await supabase
          ?.from('promo_summer_orders')
          ?.select('id', { count: 'exact', head: true })
          ?.eq('client_id', client?.id);

        // Fetch barrel orders count for this client
        const { count: barrelCount, data: barrelOrders } = await supabase
          ?.from('barrel_sales')
          ?.select('id, total_price, order_status, created_at', { count: 'exact' })
          ?.eq('client_id', client?.id);

        // Fetch carton orders count for this client
        const { count: cartonCount, data: cartonOrders } = await supabase
          ?.from('carton_sales')
          ?.select('id, total_price, created_at', { count: 'exact' })
          ?.eq('client_id', client?.id);

        // ✅ FIXED: If client has phone-based default name, try to update with actual sender name
        let updatedName = client?.name;
        if (client?.name?.startsWith('Client ') && shipments && shipments?.length > 0) {
          const shipmentWithName = shipments?.find(s => s?.sender_name && !s?.sender_name?.startsWith('Client '));
          if (shipmentWithName && shipmentWithName?.sender_name) {
            const { data: updatedClient, error: updateError } = await supabase
              ?.from('clients')
              ?.update({ name: shipmentWithName?.sender_name })
              ?.eq('id', client?.id)
              ?.select()
              ?.single();
            if (!updateError && updatedClient) {
              updatedName = updatedClient?.name;
            }
          }
        }

        const isPromoClient = client?.client_status === 'promo_customer' || (promoCount || 0) > 0;
        const isMaterialClient = ['client_materiel_livre', 'client_en_attente_collecte']?.includes(client?.client_status) || (barrelCount || 0) > 0 || (cartonCount || 0) > 0;

        // Calculate total paid from barrel/carton orders
        const barrelTotalPaid = barrelOrders?.reduce((sum, o) => sum + parseFloat(o?.total_price || 0), 0) || 0;
        const cartonTotalPaid = cartonOrders?.reduce((sum, o) => sum + parseFloat(o?.total_price || 0), 0) || 0;

        // Last material delivery date
        const allMaterialDates = [
          ...(barrelOrders?.map(o => o?.created_at) || []),
          ...(cartonOrders?.map(o => o?.created_at) || [])
        ]?.filter(Boolean)?.sort()?.reverse();
        const lastMaterialDelivery = allMaterialDates?.[0] || client?.last_material_delivery_date || null;

        return {
          id: client?.id,
          name: updatedName,
          phone: client?.phone,
          email: client?.email,
          city: client?.city,
          address: client?.address,
          postalCode: client?.postal_code,
          loyaltyPoints: client?.loyalty_points_total || 0,
          shipmentCount: shipments?.length || 0,
          promoOrdersCount: promoCount || client?.promo_orders_count || 0,
          barrelOrdersCount: barrelCount || client?.barrel_orders_count || 0,
          cartonOrdersCount: cartonCount || client?.carton_orders_count || 0,
          totalPaidAmount: barrelTotalPaid + cartonTotalPaid + (client?.total_paid_amount || 0),
          lastMaterialDelivery,
          createdAt: client?.created_at,
          referralCode: client?.referral_code,
          referredByCode: client?.referred_by_code,
          // ✅ Protection: if shipmentCount > 0, never return prospect/prospect_inscrit
          clientStatus: (shipments?.length || 0) > 0 && ['prospect', 'prospect_inscrit']?.includes(client?.client_status)
            ? 'client_avec_envoi'
            : (client?.client_status || ((shipments?.length || 0) > 0 ? 'client_avec_envoi' : 'prospect')),
          source: client?.source || null,
          isPromoClient,
          isMaterialClient,
          welcomePointsAwarded: client?.welcome_points_awarded,
          isArchived: client?.is_archived || false,
          archivedAt: client?.archived_at || null,
        };
      })
    );

    return clientsWithStats;
  },

  // Get client details with shipment history
  async getClientDetails(clientId) {
    // ✅ UTILISATION DIRECTE DU PARAMÈTRE SANS VALIDATION MANUELLE
    // Supabase va gérer la validation de l'UUID

    const { data: client, error: clientError } = await supabase
      ?.from('clients')?.select('*')?.eq('id', clientId)
      ?.single();

    if (clientError) {
      console.error('Error fetching client:', clientError);
      throw new Error('Client non trouvé');
    }

    if (!client) {
      throw new Error('Client non trouvé');
    }

    // Query shipments table directly instead of view to avoid foreign key errors
    const { data: shipments, error: shipmentsError } = await supabase
      ?.from('shipments')?.select('*')?.eq('sender_phone', client?.phone)?.order('created_at', { ascending: false });

    if (shipmentsError) throw shipmentsError;

    // Fetch promo orders linked to this client
    const { data: promoOrders } = await supabase
      ?.from('promo_summer_orders')?.select('*')?.eq('client_id', clientId)?.order('created_at', { ascending: false });

    // Fetch barrel orders linked to this client
    const { data: barrelOrders } = await supabase
      ?.from('barrel_sales')?.select('*')?.eq('client_id', clientId)?.order('created_at', { ascending: false });

    // Fetch carton orders linked to this client
    const { data: cartonOrders } = await supabase
      ?.from('carton_sales')?.select('*')?.eq('client_id', clientId)?.order('created_at', { ascending: false });

    // Fetch invoices linked to this client (unified invoices)
    const { data: invoices } = await supabase
      ?.from('adt_invoices')?.select('*')?.eq('client_id', clientId)?.order('created_at', { ascending: false });

    // Calculate totals for each shipment
    const shipmentsWithTotals = await Promise.all(
      shipments?.map(async (shipment) => {
        const { data: items } = await supabase
          ?.from('shipment_items')?.select('price_total')?.eq('shipment_id', shipment?.id);

        const totalAmount = items?.reduce((sum, item) => sum + (parseFloat(item?.price_total) || 0), 0) || 0;

        return {
          id: shipment?.id,
          reference: shipment?.reference,
          receiverName: shipment?.receiver_name,
          receiverCity: shipment?.receiver_city,
          status: shipment?.current_status,
          totalAmount,
          createdAt: shipment?.created_at
        };
      })
    );

    // Map promo orders
    const promoOrdersMapped = (promoOrders || [])?.map(order => ({
      id: order?.id,
      barrelQuantity: order?.barrel_quantity,
      totalPromoAmount: parseFloat(order?.total_promo_amount) || 0,
      totalDepositAmount: parseFloat(order?.total_deposit_amount) || 0,
      totalBalanceAmount: parseFloat(order?.total_balance_amount) || 0,
      depositPaid: order?.deposit_paid,
      balancePaid: order?.balance_paid,
      orderStatus: order?.order_status,
      deliveryAddress: order?.delivery_address,
      deliveryCity: order?.delivery_city,
      providerName: order?.provider_name,
      selectedProductName: order?.selected_product_name,
      depositInvoiceRef: order?.deposit_invoice_ref,
      createdAt: order?.created_at,
    }));

    // Map barrel orders
    const barrelOrdersMapped = (barrelOrders || [])?.map(order => ({
      id: order?.id,
      barrelType: order?.barrel_type,
      quantity: order?.quantity,
      unitPrice: parseFloat(order?.unit_price) || 0,
      totalPrice: parseFloat(order?.total_price) || 0,
      paymentStatus: order?.payment_status,
      orderStatus: order?.order_status,
      orderDate: order?.order_date,
      notes: order?.notes,
      createdAt: order?.created_at
    }));

    // Map carton orders
    const cartonOrdersMapped = (cartonOrders || [])?.map(order => ({
      id: order?.id,
      cartonType: order?.carton_type,
      quantity: order?.quantity,
      unitPrice: parseFloat(order?.unit_price) || 0,
      totalPrice: parseFloat(order?.total_price) || 0,
      paymentStatus: order?.payment_status,
      saleDate: order?.sale_date,
      notes: order?.notes,
      createdAt: order?.created_at
    }));

    // Map invoices
    const invoicesMapped = (invoices || [])?.map(inv => ({
      id: inv?.id,
      reference: inv?.invoice_reference,
      invoiceType: inv?.invoice_type,
      sourceType: inv?.source_type,
      status: inv?.invoice_status,
      totalAmount: parseFloat(inv?.total_amount) || 0,
      paidAmount: parseFloat(inv?.paid_amount) || 0,
      remainingAmount: parseFloat(inv?.remaining_amount) || 0,
      createdAt: inv?.created_at
    }));

    const totalPromoAmount = promoOrdersMapped?.reduce((sum, o) => sum + o?.totalPromoAmount, 0);
    const totalDepositPaid = promoOrdersMapped?.filter(o => o?.depositPaid)?.reduce((sum, o) => sum + o?.totalDepositAmount, 0);
    const totalBalanceDue = promoOrdersMapped?.filter(o => !o?.balancePaid)?.reduce((sum, o) => sum + o?.totalBalanceAmount, 0);

    const totalBarrelPaid = barrelOrdersMapped?.reduce((sum, o) => sum + o?.totalPrice, 0);
    const totalCartonPaid = cartonOrdersMapped?.reduce((sum, o) => sum + o?.totalPrice, 0);

    const isMaterialClient = ['client_materiel_livre', 'client_en_attente_collecte']?.includes(client?.client_status) || barrelOrdersMapped?.length > 0 || cartonOrdersMapped?.length > 0;

    return {
      id: client?.id,
      name: client?.name,
      phone: client?.phone,
      email: client?.email,
      address: client?.address,
      city: client?.city,
      postalCode: client?.postal_code,
      loyaltyPoints: client?.loyalty_points_total || 0,
      createdAt: client?.created_at,
      clientStatus: client?.client_status || 'prospect',
      source: client?.source || null,
      isPromoClient: client?.client_status === 'promo_customer' || (promoOrdersMapped?.length || 0) > 0,
      isMaterialClient,
      lastMaterialDelivery: client?.last_material_delivery_date || null,
      shipments: shipmentsWithTotals,
      promoOrders: promoOrdersMapped,
      barrelOrders: barrelOrdersMapped,
      cartonOrders: cartonOrdersMapped,
      invoices: invoicesMapped,
      promoStats: {
        totalOrders: promoOrdersMapped?.length,
        totalPromoAmount,
        totalDepositPaid,
        totalBalanceDue,
      },
      materialStats: {
        totalBarrelOrders: barrelOrdersMapped?.length,
        totalCartonOrders: cartonOrdersMapped?.length,
        totalBarrelPaid,
        totalCartonPaid,
        totalMaterialPaid: totalBarrelPaid + totalCartonPaid,
      },
    };
  },

  // Get client by ID with UUID validation
  async getClientById(clientId) {
    // Remove safeUUID call - let Supabase handle UUID validation directly
    const { data: client, error: clientError } = await supabase
      ?.from('clients')?.select('*')?.eq('id', clientId)  // Use clientId directly
      ?.single();

    if (clientError) {
      console.error('Error fetching client:', clientError);
      throw new Error('Client non trouvé');
    }

    if (!client) {
      throw new Error('Client non trouvé');
    }

    return client;
  },

  // Update client information
  async updateClient(clientId, updates) {
    // ✅ UTILISATION DIRECTE DU PARAMÈTRE
    
    const { data, error } = await supabase
      ?.from('clients')
      ?.update({
        name: updates?.name,
        email: updates?.email,
        phone: updates?.phone,
        address: updates?.address,
        city: updates?.city,
        postal_code: updates?.postalCode,
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', clientId)
      ?.select()
      ?.single();

    if (error) {
      console.error('Error updating client:', error);
      throw new Error(error?.message || 'Erreur lors de la mise à jour du client');
    }

    return data;
  },

  // Archive a client (soft delete)
  async archiveClient(clientId) {
    const { error } = await supabase
      ?.from('clients')
      ?.update({ is_archived: true, archived_at: new Date()?.toISOString() })
      ?.eq('id', clientId);
    if (error) throw new Error(error?.message || 'Erreur lors de l\'archivage');
  },

  // Restore an archived client
  async restoreClient(clientId) {
    const { error } = await supabase
      ?.from('clients')
      ?.update({ is_archived: false, archived_at: null })
      ?.eq('id', clientId);
    if (error) throw new Error(error?.message || 'Erreur lors de la restauration');
  },

  // Check if a client has commercial/logistic history before deletion
  async checkClientDependencies(clientId) {
    const checks = await Promise.all([
      supabase?.from('shipments')?.select('id', { count: 'exact', head: true })?.eq('client_id', clientId),
      supabase?.from('promo_summer_orders')?.select('id', { count: 'exact', head: true })?.eq('client_id', clientId),
      supabase?.from('barrel_sales')?.select('id', { count: 'exact', head: true })?.eq('client_id', clientId),
      supabase?.from('carton_sales')?.select('id', { count: 'exact', head: true })?.eq('client_id', clientId),
      supabase?.from('adt_invoices')?.select('id', { count: 'exact', head: true })?.eq('client_id', clientId),
    ]);

    const [shipments, promoOrders, barrelSales, cartonSales, invoices] = checks;
    const details = [];
    if ((shipments?.count || 0) > 0) details?.push(`${shipments?.count} envoi(s)`);
    if ((promoOrders?.count || 0) > 0) details?.push(`${promoOrders?.count} commande(s) promo`);
    if ((barrelSales?.count || 0) > 0) details?.push(`${barrelSales?.count} vente(s) barrique`);
    if ((cartonSales?.count || 0) > 0) details?.push(`${cartonSales?.count} vente(s) carton`);
    if ((invoices?.count || 0) > 0) details?.push(`${invoices?.count} facture(s)`);

    return {
      hasHistory: details?.length > 0,
      details: details?.join(', ')
    };
  },

  // Delete a client permanently (only if no history)
  async deleteClient(clientId) {
    const check = await this?.checkClientDependencies(clientId);
    if (check?.hasHistory) {
      throw new Error(`Ce client possède un historique (${check?.details}). Archivez-le au lieu de le supprimer.`);
    }
    const { error } = await supabase?.from('clients')?.delete()?.eq('id', clientId);
    if (error) throw new Error(error?.message || 'Erreur lors de la suppression');
  },

  // Convert a prospect to client manually
  async convertProspectToClient(clientId) {
    const { error } = await supabase
      ?.from('clients')
      ?.update({ client_status: 'client_avec_envoi', updated_at: new Date()?.toISOString() })
      ?.eq('id', clientId);
    if (error) throw new Error(error?.message || 'Erreur lors de la conversion');
  },

  // Export shipments to CSV
  exportShipmentsToCSV(shipments) {
    const headers = [
      'Référence','Expéditeur','Ville','Statut','Total (EUR)','Points Fidélité Client'
    ];

    const rows = shipments?.map(s => [
      s?.reference,
      s?.senderName,
      s?.senderCity,
      s?.status,
      s?.totalAmount?.toFixed(2) || '0.00',
      s?.clientLoyaltyPoints || 0
    ]);

    const csvContent = [
      headers?.join(','),
      ...rows?.map(row => row?.map(cell => `"${cell}"`)?.join(','))
    ]?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', `envois_${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  },

  // Send message or internal note for a shipment
  async sendMessage(shipmentId, messageData) {
    try {
      console.log('📤 Sending message for shipment:', shipmentId);
      
      if (!shipmentId) {
        throw new Error('ID envoi manquant');
      }

      if (!messageData?.content || !messageData?.content?.trim()) {
        throw new Error('Le contenu du message ne peut pas être vide');
      }

      // Insert message into database
      const { data, error } = await supabase
        ?.from('shipment_messages')
        ?.insert({
          shipment_id: shipmentId,
          message: messageData?.content?.trim(),
          sender: 'admin',  // Could be made dynamic based on logged-in admin
          is_client_visible: messageData?.isClientVisible ?? true,
          created_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (error) {
        console.error('❌ Error inserting message:', error);
        throw error;
      }

      console.log('✅ Message sent successfully:', data);
      return data;
    } catch (error) {
      console.error('❌ Error in sendMessage:', error);
      throw new Error(error?.message || 'Erreur lors de l\'envoi du message');
    }
  },

  // Get all messages for a shipment
  async getShipmentMessages(shipmentId) {
    try {
      console.log('📨 Fetching messages for shipment:', shipmentId);
      
      if (!shipmentId) {
        throw new Error('ID envoi manquant');
      }

      const { data, error } = await supabase
        ?.from('shipment_messages')
        ?.select('*')
        ?.eq('shipment_id', shipmentId)
        ?.order('created_at', { ascending: true });

      if (error) {
        console.error('❌ Error fetching messages:', error);
        throw error;
      }

      // Transform to match component expectations
      return data?.map(msg => ({
        id: msg?.id,
        content: msg?.message,
        sender: msg?.sender,
        isClientVisible: msg?.is_client_visible,
        timestamp: msg?.created_at
      })) || [];
    } catch (error) {
      console.error('❌ Error in getShipmentMessages:', error);
      throw new Error(error?.message || 'Erreur lors de la récupération des messages');
    }
  },

  // ✅ NEW: Enable WhatsApp notifications for client
  async enableWhatsAppNotifications(clientId) {
    try {
      console.log('📱 Enabling WhatsApp notifications for client:', clientId);
      
      if (!clientId) {
        throw new Error('ID client manquant');
      }

      // Update client record with whatsapp_opt_in = true and whatsapp_opt_in_at = now()
      const { data, error } = await supabase
        ?.from('clients')
        ?.update({
          whatsapp_opt_in: true,
          whatsapp_opt_in_at: new Date()?.toISOString()
        })
        ?.eq('id', clientId)
        ?.select()
        ?.single();

      if (error) {
        console.error('❌ Error enabling WhatsApp notifications:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Client non trouvé');
      }

      console.log('✅ WhatsApp notifications enabled successfully:', data);
      return data;
    } catch (error) {
      console.error('❌ Error in enableWhatsAppNotifications:', error);
      throw new Error(error?.message || 'Erreur lors de l\'activation des notifications WhatsApp');
    }
  },

  // ✅ NEW: Update shipment status
  async updateShipmentStatus(shipmentId, newStatus) {
    try {
      console.log('🔄 Updating status for shipment:', shipmentId);
      
      if (!shipmentId) {
        throw new Error('ID envoi manquant');
      }

      // Update shipment status in database
      const { data, error } = await supabase
        ?.from('shipments')
        ?.update({ current_status: newStatus })
        ?.eq('id', shipmentId)
        ?.select()
        ?.single();

      if (error) {
        console.error('❌ Error updating shipment status:', error);
        throw error;
      }

      console.log('✅ Status updated successfully:', data);
      return data;
    } catch (error) {
      console.error('❌ Error in updateShipmentStatus:', error);
      throw new Error(error?.message || 'Erreur lors de la mise à jour du statut');
    }
  },

  // Archive a shipment (soft delete)
  async archiveShipment(shipmentId) {
    const { error } = await supabase
      ?.from('shipments')
      ?.update({ is_archived: true, archived_at: new Date()?.toISOString() })
      ?.eq('id', shipmentId);
    if (error) throw error;
  },

  // Restore an archived shipment
  async restoreShipment(shipmentId) {
    const { error } = await supabase
      ?.from('shipments')
      ?.update({ is_archived: false, archived_at: null })
      ?.eq('id', shipmentId);
    if (error) throw error;
  },

  // Check if a shipment has financial/logistic history before deletion
  async checkShipmentDependencies(shipmentId) {
    const checks = await Promise.all([
      supabase?.from('shipment_finance')?.select('id', { count: 'exact', head: true })?.eq('shipment_id', shipmentId),
      supabase?.from('shipment_events')?.select('id', { count: 'exact', head: true })?.eq('shipment_id', shipmentId),
      supabase?.from('delivery_tasks')?.select('id', { count: 'exact', head: true })?.eq('shipment_id', shipmentId),
    ]);
    const [finance, events, tasks] = checks;
    const hasFinance = (finance?.count || 0) > 0;
    const hasEvents = (events?.count || 0) > 0;
    const hasTasks = (tasks?.count || 0) > 0;
    return { hasFinance, hasEvents, hasTasks, hasHistory: hasFinance || hasEvents || hasTasks };
  },

  // Hard delete a shipment (admin only, only if no financial/logistic history)
  async deleteShipment(shipmentId) {
    const id = shipmentId;
    await supabase?.from('shipment_messages')?.delete()?.eq('shipment_id', id);
    await supabase?.from('shipment_events')?.delete()?.eq('shipment_id', id);
    await supabase?.from('shipment_files')?.delete()?.eq('shipment_id', id);
    await supabase?.from('shipment_items')?.delete()?.eq('shipment_id', id);
    await supabase?.from('whatsapp_notifications')?.delete()?.eq('shipment_id', id);
    await supabase?.from('delivery_tasks')?.delete()?.eq('shipment_id', id);
    await supabase?.from('professional_client_notifications')?.delete()?.eq('shipment_id', id);
    await supabase?.from('pro_communications')?.delete()?.eq('shipment_id', id);
    await supabase?.from('pro_documents')?.delete()?.eq('shipment_id', id);
    await supabase?.from('pro_ex1_tracking')?.delete()?.eq('shipment_id', id);
    await supabase?.from('shipment_finance')?.delete()?.eq('shipment_id', id);
    await supabase?.from('pickup_tasks')?.update({ shipment_id: null })?.eq('shipment_id', id);
    const { error } = await supabase?.from('shipments')?.delete()?.eq('id', id);
    if (error) throw error;
  }
};